import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PeopleIcon from '@mui/icons-material/People';
import BarChartIcon from '@mui/icons-material/BarChart';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { DateNavigator, formatLocalDate, getYearRange, getMonthRange, getWeekRange } from '../../components/DateNavigator/DateNavigator';
import navStyles from '../../components/DateNavigator/DateNavigator.module.css';
import { getSalesChess, SalesChessData, SalesChessParams } from './services/salesChessAPI';
import SalesChessGrid from './components/SalesChessGrid';
import SalesChessPageSkeleton from './SalesChessPageSkeleton';
import SimpleSearchBar from './components/SimpleSearchBar';
import { CarteiraSelector } from '../../components/CarteiraSelector/CarteiraSelector';
import ModernDashboardHeader from '../../components/ModernDashboardHeader/ModernDashboardHeader';
import { useAuthStore } from '../../stores/authStore';
import { useFilterStore } from '../../stores/filterStore';
import styles from './SalesChessPage.module.css';

// Cache em memória local para evitar Skeleton ao trocar de aba
let moduleCachedData: SalesChessData | null = null;
let moduleCachedParamsStr: string = '';

// Componente de Busca com Debounce Local para evitar lag de digitação no grid pesado
const DebouncedSearchBar: React.FC<{
    value: string;
    onSearch: (value: string) => void;
    placeholder?: string;
}> = React.memo(({ value, onSearch, placeholder = 'Buscar cliente...' }) => {
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    useEffect(() => {
        const handler = setTimeout(() => {
            onSearch(localValue);
        }, 400); // 400ms debounce
        return () => clearTimeout(handler);
    }, [localValue, onSearch]);

    return (
        <div className={styles.tableSearchBar}>
            <SearchIcon className={styles.tableSearchIcon} />
            <input
                type="text"
                placeholder={placeholder}
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                className={styles.tableSearchInput}
            />
            {localValue && (
                <ClearIcon
                    className={styles.tableSearchClear}
                    onClick={() => setLocalValue('')}
                />
            )}
        </div>
    );
});

const SalesChessPage: React.FC = () => {
    const [data, setData] = useState<SalesChessData | null>(moduleCachedData);
    const [loading, setLoading] = useState(!moduleCachedData);
    const [loadingMore, setLoadingMore] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            const params: SalesChessParams = {
                carteiraId: selectedCarteiraId,
                page: 1,
                limit,
                search: searchTerm || undefined,
                sortBy,
                sortOrder,
                dataI: startDate,
                dataF: endDate,
                periodo,
                refresh: true
            };
            const result = await getSalesChess(params);
            setData(result);
            setCurrentPage(1);
            setHasMore(result.pagination.currentPage < result.pagination.totalPages);
            
            moduleCachedData = result;
            moduleCachedParamsStr = JSON.stringify({ ...params, refresh: undefined });
        } catch (err) {
            console.error('Erro ao atualizar cache do xadrez de vendas:', err);
        } finally {
            setIsRefreshing(false);
        }
    };

    // Filtros e paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState<string>('total');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [limit] = useState(20);

    const role = useAuthStore(state => state.role);
    const selectedCarteiraId = useFilterStore(state => state.selectedCarteiraId);

    const isSupervisor = role === 'supervisor' || role === 'diretor';
    const isFiscal = !isSupervisor || !!selectedCarteiraId;

    // ── DateNavigator State ──
    const [periodo, setPeriodo] = useState<'12meses' | 'anual' | 'mensal' | 'custom'>('12meses');
    const [dataRef, setDataRef] = useState<string>(() => formatLocalDate(new Date()));
    const [dataInicio, setDataInicio] = useState<string>('');
    const [dataFim, setDataFim] = useState<string>('');

    const { startDate, endDate } = useMemo(() => {
        if (periodo === 'custom') {
            return { startDate: dataInicio, endDate: dataFim };
        }
        const dRef = dataRef || formatLocalDate(new Date());
        if (periodo === '12meses') {
            if (isFiscal) {
                const d = new Date(dRef + 'T12:00:00');
                const year = d.getFullYear();
                const month = d.getMonth();
                const start = new Date(year, month - 12, 26, 0, 0, 0, 0);
                const end = new Date(year, month, 25, 23, 59, 59, 999);
                return {
                    startDate: formatLocalDate(start),
                    endDate: formatLocalDate(end)
                };
            } else {
                const range = getMonthRange(dRef);
                const startOfSelectedMonth = new Date(range.start);
                const startOf12MonthsAgo = new Date(
                    startOfSelectedMonth.getFullYear(),
                    startOfSelectedMonth.getMonth() - 11,
                    startOfSelectedMonth.getDate()
                );
                return {
                    startDate: formatLocalDate(startOf12MonthsAgo),
                    endDate: formatLocalDate(range.end)
                };
            }
        }
        let range: { start: Date; end: Date };
        if (periodo === 'mensal') {
            if (isFiscal) {
                const d = new Date(dRef + 'T12:00:00');
                const year = d.getFullYear();
                const month = d.getMonth();
                range = {
                    start: new Date(year, month - 1, 26, 0, 0, 0, 0),
                    end: new Date(year, month, 25, 23, 59, 59, 999)
                };
            } else {
                range = getMonthRange(dRef);
            }
        } else {
            range = getYearRange(dRef);
        }
        return {
            startDate: formatLocalDate(range.start),
            endDate: formatLocalDate(range.end),
        };
    }, [periodo, dataRef, dataInicio, dataFim, isFiscal]);

    // Fetch quando os valores mudarem
    const fetchData = useCallback(async (isReset: boolean = false) => {
        const params: SalesChessParams = {
            carteiraId: selectedCarteiraId,
            page: currentPage,
            limit,
            search: searchTerm || undefined,
            sortBy,
            sortOrder,
            dataI: startDate,
            dataF: endDate,
            periodo
        };
        const paramsStr = JSON.stringify(params);

        if (isReset) {
            if (!moduleCachedData || moduleCachedParamsStr !== paramsStr) {
                setLoading(true); // Loading principal (tela cheia/spinner)
            }
        } else {
            setLoadingMore(true); // Loading secundário (botão)
        }
        setError(null);

        try {
            const result = await getSalesChess(params);

            if (isReset) {
                setData(result);
                moduleCachedData = result;
                moduleCachedParamsStr = JSON.stringify(params);
            } else {
                setData(prevData => ({
                    ...result,
                    clients: [...(prevData?.clients || []), ...result.clients],
                    salesData: {
                        ...(prevData?.salesData || {}),
                        ...result.salesData
                    }
                }));
            }

            setHasMore(result.pagination.currentPage < result.pagination.totalPages);

        } catch (err: any) {
            console.error('Erro ao carregar xadrez de vendas:', err);
            setError('Erro ao carregar os dados. Tente novamente.');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [
        selectedCarteiraId,
        currentPage,
        limit,
        searchTerm,
        sortBy,
        sortOrder,
        startDate,
        endDate,
        periodo
    ]);

    // Resetar para página 1 ao alterar filtros
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedCarteiraId, startDate, endDate, periodo]);

    useEffect(() => {
        const isReset = currentPage === 1;
        fetchData(isReset);
    }, [
        fetchData,
        currentPage
    ]);

    const handleSearch = useCallback((term: string) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    const handleSort = useCallback((column: string) => {
        setSortBy(prev => {
            if (prev === column) {
                setSortOrder(order => order === 'asc' ? 'desc' : 'asc');
            } else {
                setSortOrder('desc');
            }
            return column;
        });
        setCurrentPage(1);
    }, []);

    const handleLoadMore = useCallback(() => {
        if (!loadingMore && hasMore) {
            setCurrentPage(prevPage => prevPage + 1);
        }
    }, [loadingMore, hasMore]);

    if (loading && !data) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.filterBar}>
                    <div className={styles.filterGroup}>
                        <div className={navStyles.periodContainer}>
                            {(['12meses', 'anual', 'mensal', 'custom'] as const).map((p) => (
                                <button
                                    key={p}
                                    onClick={() => {
                                        setPeriodo(p);
                                        if (p !== 'custom') {
                                            setDataInicio('');
                                            setDataFim('');
                                        }
                                    }}
                                    className={`${navStyles.periodButton} ${periodo === p ? navStyles.periodButtonActive : ''}`}
                                >
                                    {p === '12meses' ? 'Últimos 12 Meses' : p === 'custom' ? 'Personalizado' : p.charAt(0).toUpperCase() + p.slice(1)}
                                </button>
                            ))}
                        </div>

                        <DateNavigator
                            label=""
                            periodo={periodo}
                            dataRef={dataRef}
                            dataInicio={dataInicio}
                            dataFim={dataFim}
                            isFiscal={isFiscal}
                            onChange={(vals) => {
                                if (vals.periodo) setPeriodo(vals.periodo);
                                if (vals.dataRef) setDataRef(vals.dataRef);
                                if (vals.dataInicio) setDataInicio(vals.dataInicio);
                                if (vals.dataFim) setDataFim(vals.dataFim);
                            }}
                        />
                    </div>

                    <ModernDashboardHeader 
                        variant="minimal" 
                        cacheInfo={{
                            cached: false,
                            cachedAt: undefined,
                            onRefresh: handleRefresh,
                            isRefreshing: isRefreshing
                        }}
                    />
                </div>

                <div className={styles.tableToolbar}>
                    <DebouncedSearchBar value={searchTerm} onSearch={handleSearch} />
                    {isSupervisor && <CarteiraSelector />}
                </div>

                <SalesChessPageSkeleton />
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.errorContainer}>
                <p>{error}</p>
                {/* CORREÇÃO AQUI */}
                <button onClick={() => fetchData(true)} className={styles.retryButton}>
                    Tentar Novamente
                </button>
            </div>
        );
    }

    const formatMonth = (month: string) => {
        if (!month) return '';
        if (month.startsWith('Semana')) {
            return month;
        }
        const parts = month.split('-');
        const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
            'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        
        if (parts.length === 3) {
            const [year, monthNum, day] = parts;
            return `${day}/${monthNames[parseInt(monthNum, 10) - 1]}`;
        }
        if (parts.length === 2) {
            const [year, monthNum] = parts;
            return `${monthNames[parseInt(monthNum, 10) - 1]}/${year.slice(2)}`;
        }
        return month;
    };

    const formatDateBR = (dateStr: string) => {
        if (!dateStr) return '';
        const parts = dateStr.split('-');
        if (parts.length !== 3) return dateStr;
        return `${parts[2]}/${parts[1]}/${parts[0].slice(2)}`;
    };

    return (
        <div className={styles.pageContainer}>
            <div className={styles.filterBar}>
                <div className={styles.filterTopRow}>
                    <div className={styles.filterGroup}>
                        <div className={navStyles.periodContainer}>
                            {(['12meses', 'anual', 'mensal', 'custom'] as const).map((p) => (
                                <button
                                    key={p}
                                    onClick={() => {
                                        setPeriodo(p);
                                        if (p !== 'custom') {
                                            setDataInicio('');
                                            setDataFim('');
                                        }
                                    }}
                                    className={`${navStyles.periodButton} ${periodo === p ? navStyles.periodButtonActive : ''}`}
                                >
                                    {p === '12meses' ? 'Últimos 12 Meses' : p === 'custom' ? 'Personalizado' : p.charAt(0).toUpperCase() + p.slice(1)}
                                </button>
                            ))}
                        </div>

                        <DateNavigator
                            label=""
                            periodo={periodo}
                            dataRef={dataRef}
                            dataInicio={dataInicio}
                            dataFim={dataFim}
                            isFiscal={isFiscal}
                            onChange={(vals) => {
                                if (vals.periodo) setPeriodo(vals.periodo);
                                if (vals.dataRef) setDataRef(vals.dataRef);
                                if (vals.dataInicio) setDataInicio(vals.dataInicio);
                                if (vals.dataFim) setDataFim(vals.dataFim);
                            }}
                        />
                    </div>

                    <ModernDashboardHeader 
                        variant="minimal" 
                        cacheInfo={{
                            cached: data?.cached,
                            cachedAt: data?.cachedAt,
                            onRefresh: handleRefresh,
                            isRefreshing: isRefreshing
                        }}
                    />
                </div>
                {data && (
                    <div className={styles.kpiBottomRow}>
                        <span className={styles.kpiMicroItem}>
                            <PeopleIcon style={{ fontSize: 16, color: '#64748b' }} />
                            <span className={styles.kpiMicroLabel}>Total:</span>
                            <span className={styles.kpiMicroValue}>{data.pagination.totalItems || 0}</span>
                        </span>
                        <span className={styles.kpiMicroItem}>
                            <BarChartIcon style={{ fontSize: 16, color: '#64748b' }} />
                            <span className={styles.kpiMicroLabel}>Exibindo:</span>
                            <span className={styles.kpiMicroValue}>{data.clients.length || 0}</span>
                        </span>
                        {data.months && data.months.length > 0 && (
                            <span className={styles.kpiMicroItem}>
                                <CalendarMonthIcon style={{ fontSize: 16, color: '#64748b' }} />
                                <span className={styles.kpiMicroLabel}>Período:</span>
                                <span className={styles.kpiMicroValue}>
                                    {periodo === 'mensal' ? (
                                        `${formatDateBR(startDate)} a ${formatDateBR(endDate)}`
                                    ) : (
                                        `${formatMonth(data.months[0])} a ${formatMonth(data.months[data.months.length - 1])}`
                                    )}
                                </span>
                            </span>
                        )}
                    </div>
                )}
            </div>

            <div className={styles.gridScrollContainer}>
                {loading && data && (
                    <div className={styles.linearLoader}>
                        <div className={styles.linearLoaderBar} />
                    </div>
                )}
                <div className={styles.tableToolbar}>
                    <DebouncedSearchBar value={searchTerm} onSearch={handleSearch} />
                    {isSupervisor && <CarteiraSelector />}

                    {/* kpiInlineGroup removed */}
                </div>
                <div className={styles.gridScrollInner}>
                {data && (
                    <>
                        <SalesChessGrid
                            clients={data.clients}
                            months={data.months}
                            salesData={data.salesData}
                            monthlyTotals={data.monthlyTotals}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleSort}
                            hasMore={hasMore}
                            loadingMore={loadingMore}
                            onLoadMore={handleLoadMore}
                            startDate={startDate}
                            endDate={endDate}
                        />
                    </>
                )}
            </div>
            </div>

            {/* O "Carregar Mais" foi MOVIDO para dentro do .gridScrollContainer */}
        </div>
    );
};

export default SalesChessPage;